import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useCatalogStore } from '@/modules/catalog/public'
import { useAppNotifications } from '@/modules/common/public'
import { useFeatureFlags } from '@/modules/common/public'
import {
  buildActiveFilterChips,
  buildAttributeFilterParams as buildAttributeFilterParamsPure,
  countActiveFilters,
  hasAdvancedFilters as hasAdvancedFiltersPure,
  hasAttributeSelections as hasAttributeSelectionsPure,
  normalizeSelectValue,
  pluralize,
  type ActiveFilterChip,
  type DynamicAttributeFilter,
} from '@/modules/catalog/model/catalogFilters'

export function useCatalogView() {
  const route = useRoute()
  const router = useRouter()
  const catalogStore = useCatalogStore()
  const notificationStore = useAppNotifications()
  const featureFlags = useFeatureFlags()

  const loading = ref(false)
  const viewMode = ref('grid')
  const currentPage = ref(1)
  const sortBy = ref('name_asc')
  const searchQuery = ref('')
  let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null

  const filters = ref({
    search: '',
    categories: [] as string[],
    manufacturers: [] as string[],
    priceRange: [0, 150000] as [number, number],
    selectedCategory: null as string | null,
    selectedSupplier: null as string | null,
  })
  const inStock = ref(false)
  const attributeValueFilters = ref<Record<string, string[]>>({})
  const attributeRangeFilters = ref<Record<string, { min: string; max: string }>>({})
  const attributeBooleanFilters = ref<Record<string, string>>({})

  const showAdvancedFilters = ref(false)
  const isAdvancedFiltersEnabled = computed(() => featureFlags.isEnabled('advanced_filters'))

  type CategoryItem = {
    id: string
    name: string
    slug?: string
    description?: string
    level?: number
    productCount: number
  }

  type ManufacturerItem = {
    id: string
    name: string
    slug?: string
    productCount?: number
  }

  const categories = ref<CategoryItem[]>([])
  const manufacturers = ref<ManufacturerItem[]>([])
  const priceRange = ref({ min: 0, max: 150000 })
  const suppliers = computed(() => catalogStore.suppliers)
  const dynamicAttributeFilters = computed(
    () => catalogStore.categoryAttributeFilters as DynamicAttributeFilter[],
  )

  const supplierSelectOptions = computed(() => [
    { label: 'Все поставщики', value: '' },
    ...suppliers.value.map((supplier) => ({ label: supplier.name, value: supplier.id })),
  ])

  const categorySelectOptions = computed(() => [
    { label: 'Все категории', value: '' },
    ...categories.value.map((category) => ({ label: category.name, value: category.id })),
  ])

  const sortSelectOptions = [
    { label: 'По названию (А-Я)', value: 'name_asc' },
    { label: 'По названию (Я-А)', value: 'name_desc' },
    { label: 'По цене (возрастание)', value: 'price_asc' },
    { label: 'По цене (убывание)', value: 'price_desc' },
    { label: 'По популярности', value: 'popularity_desc' },
    { label: 'Новинки', value: 'created_desc' },
  ]
  const products = computed(() => catalogStore.filteredProducts)
  const totalProducts = computed(() => catalogStore.totalCount)
  const totalPages = computed(() => catalogStore.paginationInfo.totalPages)
  const sectionSubtitle = computed(() =>
    totalProducts.value > 0
      ? `${totalProducts.value} ${pluralize(totalProducts.value, 'товар', 'товара', 'товаров')}`
      : '',
  )

  const currentCategory = computed(() => {
    const categoryId = route.params['categoryId'] as string | undefined
    if (categoryId) {
      return categories.value.find((category) => category.id === categoryId) || null
    }

    const selectedCategoryId = normalizeSelectValue(filters.value.selectedCategory)
    if (selectedCategoryId) {
      return categories.value.find((category) => category.id === selectedCategoryId) || null
    }

    return null
  })

  const hasActiveFilters = computed(() => {
    const selectedCategoryId = normalizeSelectValue(filters.value.selectedCategory)
    const selectedSupplierId = normalizeSelectValue(filters.value.selectedSupplier)

    return (
      filters.value.search !== '' ||
      filters.value.categories.length > 0 ||
      filters.value.manufacturers.length > 0 ||
      filters.value.priceRange[0] > priceRange.value.min ||
      filters.value.priceRange[1] < priceRange.value.max ||
      inStock.value ||
      selectedCategoryId !== null ||
      selectedSupplierId !== null ||
      hasAttributeSelections.value
    )
  })

  const hasAttributeSelections = computed(() =>
    hasAttributeSelectionsPure(
      attributeValueFilters.value,
      attributeRangeFilters.value,
      attributeBooleanFilters.value,
    ),
  )

  const hasAdvancedFilters = computed(() =>
    hasAdvancedFiltersPure(filters.value.manufacturers, filters.value.priceRange, priceRange.value),
  )

  const activeFiltersCount = computed(() =>
    countActiveFilters(filters.value.manufacturers, filters.value.priceRange, priceRange.value),
  )

  const activeFilterChips = computed<ActiveFilterChip[]>(() =>
    buildActiveFilterChips(
      {
        search: filters.value.search,
        selectedCategory: filters.value.selectedCategory,
        selectedSupplier: filters.value.selectedSupplier,
        manufacturers: filters.value.manufacturers,
        priceRange: filters.value.priceRange,
        inStock: inStock.value,
        attributeValues: attributeValueFilters.value,
        attributeRanges: attributeRangeFilters.value,
        attributeBooleans: attributeBooleanFilters.value,
      },
      priceRange.value,
      {
        categoryName: getCategoryName,
        manufacturerName: getManufacturerName,
        supplierName: getSupplierName,
        attributes: dynamicAttributeFilters.value,
      },
    ),
  )

  const loadProducts = async () => {
    loading.value = true
    try {
      catalogStore.setSearch(filters.value.search, false)
      catalogStore.setCategory(normalizeSelectValue(filters.value.selectedCategory), false)
      catalogStore.setSupplier(normalizeSelectValue(filters.value.selectedSupplier), false)
      catalogStore.setManufacturers(filters.value.manufacturers, false)
      catalogStore.setInStock(inStock.value, false)
      catalogStore.setSorting(sortBy.value, 'asc', false)
      catalogStore.setPage(currentPage.value, false)

      await catalogStore.fetchProducts()
    } catch (error) {
      console.error('Ошибка загрузки товаров:', error)
      notificationStore.showError('Ошибка загрузки товаров')
    } finally {
      loading.value = false
    }
  }

  const loadFiltersData = async () => {
    try {
      // Категории и производители уже загружены через catalogStore.initCatalog()
      // перед вызовом этой функции - здесь просто маппим данные из стора.
      categories.value = catalogStore.categories.map((category) => ({
        id: String(category.id),
        name: category.name,
        slug: category.slug,
        ...(typeof category.description === 'string' ? { description: category.description } : {}),
        productCount: category.productCount,
      }))

      manufacturers.value = catalogStore.manufacturers

      priceRange.value = { min: 0, max: 150000 }
      filters.value.priceRange = [0, 150000]
    } catch (error) {
      console.error('Ошибка загрузки данных фильтров:', error)
    }
  }

  const loadCategoryAttributeFilters = async () => {
    const categoryId = normalizeSelectValue(filters.value.selectedCategory)
    await catalogStore.fetchCategoryAttributeFilters(categoryId)
  }

  const updateMinPrice = (value: string | number) => {
    const numValue = typeof value === 'string' ? parseInt(value) : value
    if (
      !isNaN(numValue) &&
      numValue >= priceRange.value.min &&
      numValue <= filters.value.priceRange[1]
    ) {
      filters.value.priceRange[0] = numValue
      updateFilters()
    }
  }

  const updateMaxPrice = (value: string | number) => {
    const numValue = typeof value === 'string' ? parseInt(value) : value
    if (
      !isNaN(numValue) &&
      numValue <= priceRange.value.max &&
      numValue >= filters.value.priceRange[0]
    ) {
      filters.value.priceRange[1] = numValue
      updateFilters()
    }
  }

  const toggleManufacturer = (manufacturerId: string) => {
    if (filters.value.manufacturers.includes(manufacturerId)) {
      filters.value.manufacturers = filters.value.manufacturers.filter(
        (id) => id !== manufacturerId,
      )
    } else {
      filters.value.manufacturers = [...filters.value.manufacturers, manufacturerId]
    }
    updateFilters()
  }

  const toggleAttributeOption = (attributeId: number, option: string) => {
    const key = String(attributeId)
    const current = attributeValueFilters.value[key] ?? []

    attributeValueFilters.value[key] = current.includes(option)
      ? current.filter((item) => item !== option)
      : [...current, option]

    applyAttributeFilters()
  }

  const updateAttributeRangeMin = (attributeId: number, value: string | number) => {
    const key = String(attributeId)
    const range = attributeRangeFilters.value[key] ?? { min: '', max: '' }
    attributeRangeFilters.value[key] = {
      ...range,
      min: String(value ?? ''),
    }
    applyAttributeFilters()
  }

  const updateAttributeRangeMax = (attributeId: number, value: string | number) => {
    const key = String(attributeId)
    const range = attributeRangeFilters.value[key] ?? { min: '', max: '' }
    attributeRangeFilters.value[key] = {
      ...range,
      max: String(value ?? ''),
    }
    applyAttributeFilters()
  }

  const setAttributeBooleanFilter = (attributeId: number, value: string) => {
    const key = String(attributeId)
    attributeBooleanFilters.value[key] = value
    applyAttributeFilters()
  }

  const clearAttributeFilters = () => {
    attributeValueFilters.value = {}
    attributeRangeFilters.value = {}
    attributeBooleanFilters.value = {}
    applyAttributeFilters()
  }

  const buildAttributeFilterParams = (): Record<string, string | string[]> =>
    buildAttributeFilterParamsPure(
      attributeValueFilters.value,
      attributeRangeFilters.value,
      attributeBooleanFilters.value,
    )

  const applyAttributeFilters = () => {
    catalogStore.setAttributeFilterParams(buildAttributeFilterParams(), false)
    updateFilters()
  }

  const removeActiveFilter = (chip: ActiveFilterChip) => {
    switch (chip.type) {
      case 'search':
        clearSearch()
        return
      case 'category':
        filters.value.categories = []
        filters.value.selectedCategory = null
        onCategoryChange()
        return
      case 'manufacturer':
        filters.value.manufacturers = filters.value.manufacturers.filter(
          (manufacturerId) => manufacturerId !== chip.manufacturerId,
        )
        updateFilters()
        return
      case 'supplier':
        filters.value.selectedSupplier = null
        updateFilters()
        return
      case 'in_stock':
        inStock.value = false
        onInStockChange()
        return
      case 'price_range':
        filters.value.priceRange = [priceRange.value.min, priceRange.value.max]
        updateFilters()
        return
      case 'attribute_value':
        toggleAttributeOption(Number(chip.attributeId), chip.value)
        return
      case 'attribute_range':
        attributeRangeFilters.value[chip.attributeId] = { min: '', max: '' }
        applyAttributeFilters()
        return
      case 'attribute_bool':
        attributeBooleanFilters.value[chip.attributeId] = ''
        applyAttributeFilters()
        return
    }
  }

  const clearFilters = () => {
    filters.value = {
      search: '',
      categories: [],
      manufacturers: [],
      priceRange: [priceRange.value.min, priceRange.value.max],
      selectedCategory: null,
      selectedSupplier: null,
    }
    inStock.value = false
    currentPage.value = 1
    sortBy.value = 'name_asc'

    router
      .replace({
        path: route.path,
        query: {},
      })
      .catch(() => {})

    catalogStore.resetFilters(false)
    attributeValueFilters.value = {}
    attributeRangeFilters.value = {}
    attributeBooleanFilters.value = {}
    catalogStore.setAttributeFilterParams({}, false)

    loadProducts()
  }

  const clearAdvancedFilters = () => {
    filters.value.manufacturers = []
    filters.value.priceRange = [priceRange.value.min, priceRange.value.max]
    updateFilters()
  }

  const getCategoryName = (categoryId: string | null): string => {
    if (!categoryId) return ''
    const category = categories.value.find((category) => category.id === categoryId)
    return category ? category.name : ''
  }

  const getManufacturerName = (manufacturerId: string): string => {
    const manufacturer = manufacturers.value.find(
      (manufacturer) => manufacturer.id === manufacturerId,
    )
    return manufacturer ? manufacturer.name : ''
  }

  const getSupplierName = (supplierId: string): string => {
    const supplier = suppliers.value.find((supplier) => supplier.id === supplierId)
    return supplier ? supplier.name : ''
  }

  const updateFilters = () => {
    currentPage.value = 1
    updateUrl()
    loadProducts()
  }

  const onCategoryChange = () => {
    attributeValueFilters.value = {}
    attributeRangeFilters.value = {}
    attributeBooleanFilters.value = {}
    catalogStore.setAttributeFilterParams({}, false)
    void loadCategoryAttributeFilters()
    updateFilters()
  }

  const onSortChange = () => {
    updateFilters()
  }

  const onSupplierChange = () => {
    updateFilters()
  }

  const onInStockChange = () => {
    catalogStore.setInStock(inStock.value)
  }

  const updateUrl = () => {
    const currentQuery = { ...route.query }

    const searchTerm = filters.value.search?.trim()
    if (searchTerm) {
      currentQuery['q'] = searchTerm
    } else {
      delete currentQuery['q']
    }

    const categoryId = normalizeSelectValue(filters.value.selectedCategory)
    if (categoryId) {
      currentQuery['category'] = categoryId
    } else {
      delete currentQuery['category']
    }

    // API принимает одного производителя, поэтому в URL живёт только единичный выбор
    const [singleManufacturer] = filters.value.manufacturers
    if (filters.value.manufacturers.length === 1 && singleManufacturer) {
      currentQuery['manufacturer'] = singleManufacturer
    } else {
      delete currentQuery['manufacturer']
    }

    Object.keys(currentQuery).forEach((key) => {
      if (key.startsWith('attr_')) {
        delete currentQuery[key]
      }
    })

    const attributeParams = buildAttributeFilterParams()
    for (const [key, value] of Object.entries(attributeParams)) {
      currentQuery[key] = value
    }

    router
      .replace({
        path: route.path,
        query: currentQuery,
      })
      .catch(() => {})
  }

  const goToPage = async (page: number) => {
    if (loading.value) return
    if (page < 1 || page > totalPages.value || page === currentPage.value) return

    const scroll_top = window.scrollY
    currentPage.value = page
    await loadProducts()
    await nextTick()
    window.scrollTo({ top: scroll_top, behavior: 'auto' })
  }

  const prevPage = async () => {
    if (currentPage.value > 1) {
      await goToPage(currentPage.value - 1)
    }
  }

  const nextPage = async () => {
    if (currentPage.value < totalPages.value) {
      await goToPage(currentPage.value + 1)
    }
  }

  const onSearchInput = () => {
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer)
    }
    searchDebounceTimer = setTimeout(() => {
      performSearch()
    }, 500)
  }

  const performSearch = () => {
    filters.value.search = searchQuery.value
    updateFilters()
  }

  const clearSearch = () => {
    searchQuery.value = ''
    filters.value.search = ''
    updateFilters()
  }

  onMounted(async () => {
    await catalogStore.initCatalog()
    await loadFiltersData()

    const queryCategory = route.query['category'] as string | undefined
    if (queryCategory) {
      filters.value.selectedCategory = queryCategory
      filters.value.categories = [queryCategory]
      catalogStore.setCategory(queryCategory, false)
    }

    const categoryId = route.params['categoryId'] as string | undefined
    if (categoryId) {
      filters.value.categories = [categoryId]
      filters.value.selectedCategory = categoryId
      catalogStore.setCategory(categoryId, false)
    }

    await loadCategoryAttributeFilters()

    // Ссылка на производителя с карточки товара: /catalog?manufacturer=<id>
    const queryManufacturer = route.query['manufacturer']
    if (typeof queryManufacturer === 'string' && /^\d+$/.test(queryManufacturer)) {
      filters.value.manufacturers = [queryManufacturer]
    }

    const queryEntries = Object.entries(route.query)
    for (const [key, value] of queryEntries) {
      if (!key.startsWith('attr_')) {
        continue
      }

      const stringValues = Array.isArray(value) ? value.map(String) : [String(value)]
      if (/_bool$/.test(key)) {
        const attrId = key.match(/^attr_(\d+)_bool$/)?.[1]
        if (attrId) {
          attributeBooleanFilters.value[attrId] = stringValues[0] ?? ''
        }
        continue
      }

      if (/_min$/.test(key)) {
        const attrId = key.match(/^attr_(\d+)_min$/)?.[1]
        if (attrId) {
          attributeRangeFilters.value[attrId] = {
            ...(attributeRangeFilters.value[attrId] ?? { min: '', max: '' }),
            min: stringValues[0] ?? '',
          }
        }
        continue
      }

      if (/_max$/.test(key)) {
        const attrId = key.match(/^attr_(\d+)_max$/)?.[1]
        if (attrId) {
          attributeRangeFilters.value[attrId] = {
            ...(attributeRangeFilters.value[attrId] ?? { min: '', max: '' }),
            max: stringValues[0] ?? '',
          }
        }
        continue
      }

      const attrId = key.match(/^attr_(\d+)$/)?.[1]
      if (attrId) {
        attributeValueFilters.value[attrId] = stringValues.filter((item) => item.trim() !== '')
      }
    }

    catalogStore.setAttributeFilterParams(buildAttributeFilterParams(), false)

    const urlSearchQuery = route.query['q'] as string | undefined
    if (urlSearchQuery) {
      searchQuery.value = urlSearchQuery
      filters.value.search = urlSearchQuery
      catalogStore.setSearch(urlSearchQuery, false)
    }

    await loadProducts()
  })

  watch(
    () => route.params['categoryId'],
    (newCategoryId) => {
      if (typeof newCategoryId === 'string' && newCategoryId) {
        filters.value.categories = [newCategoryId]
        filters.value.selectedCategory = newCategoryId
        catalogStore.setCategory(newCategoryId, false)
        updateFilters()
      } else {
        clearFilters()
      }
    },
  )

  watch(
    () => route.query['q'],
    (newQuery) => {
      const searchQuery = (newQuery as string) || ''
      const currentSearch = filters.value.search || ''

      if (searchQuery !== currentSearch) {
        filters.value.search = searchQuery
        loadProducts()
      }
    },
  )

  return {
    activeFilterChips,
    activeFiltersCount,
    attributeBooleanFilters,
    attributeRangeFilters,
    attributeValueFilters,
    categorySelectOptions,
    clearAdvancedFilters,
    clearAttributeFilters,
    clearFilters,
    clearSearch,
    currentCategory,
    currentPage,
    dynamicAttributeFilters,
    filters,
    goToPage,
    hasActiveFilters,
    hasAdvancedFilters,
    hasAttributeSelections,
    inStock,
    isAdvancedFiltersEnabled,
    loading,
    manufacturers,
    nextPage,
    onCategoryChange,
    onInStockChange,
    onSearchInput,
    onSortChange,
    onSupplierChange,
    performSearch,
    prevPage,
    products,
    removeActiveFilter,
    searchQuery,
    setAttributeBooleanFilter,
    sectionSubtitle,
    showAdvancedFilters,
    sortBy,
    sortSelectOptions,
    supplierSelectOptions,
    toggleAttributeOption,
    toggleManufacturer,
    totalPages,
    updateAttributeRangeMax,
    updateAttributeRangeMin,
    updateMaxPrice,
    updateMinPrice,
    viewMode,
  }
}
